import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import {
	CallActivityPerformanceFragment,
	LongevityFragment,
	TopPerformerBonusFragment,
	UserFullNameFragment,
	InputValueFragment,
	ModelYearsFragment,
	CommissionTiersFragment,
	MetricFragment,
	VolumeBonusFragment,
	BonusesTiersFragment,
	BonusDefinitionFragment,
	VariableOperationsGpsBonusSetupFragment,
} from "@PayPlans/api/fragments/commons";

import {
	DefaultCommissionsFragment,
	MiniDealFragment,
	ModelBasedFragment,
	DemoAllowanceFragment,
	PacksFragment,
	AutoAlertsFragment,
	CustomerSatisfactionFragment,
	DefaultCommissionsTierFragment,
	AdjustmentTierFragment,
} from "@PayPlans/api/fragments/salesAssociates";

// Add new sales pay plans
export async function addSalesPayPlan({ locationId }) {
	const query = gql`
		mutation createSalesPayPlan($locationId: String!) {
			createSalesPayPlan(locationId: $locationId) {
				createdAt
				name
				formatVersion
			}
		}
	`;

	const variables = { locationId };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createSalesPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

export async function updateSalesAvailablePayPlan({ locationId, information }) {
	const { _id, updatedBy, available } = information;
	const query = gql`
		mutation updateSalesAvailablePayPlan($locationId: String!, $input: UpdateAvailablePayPlanInput!) {
			updateSalesAvailablePayPlan(locationId: $locationId, input: $input) {
				_id
				locationId
				name
				position
				available
			}
		}
	`;

	const variables = {
		locationId,
		input: {
			_id,
			updatedBy,
			available,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSalesAvailablePayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Add new sales pay plans
export async function cloneSalesPayPlan({ locationId, information }) {
	const { _id, createdBy } = information;
	const query = gql`
		mutation cloneSalesPayPlan($locationId: String!, $input: ClonePayPlan!) {
			cloneSalesPayPlan(locationId: $locationId, input: $input) {
				_id
				locationId
				name
				position
				default {
					...DefaultCommissions
				}
				miniDeal {
					...MiniDeal
				}
				modelBased {
					...ModelBased
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				volumeBonus {
					...VolumeBonus
				}
				demoAllowance {
					...DemoAllowance
				}
				packs {
					...Packs
				}
				autoAlerts {
					...AutoAlerts
				}
				topSalesBonus {
					...TopPerformerBonus
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				longevity {
					...Longevity
				}
				formatVersion
				createdAt
				createdBy {
					...UserFullName
				}
				updatedAt
				updatedBy {
					...UserFullName
				}
				locked
				available
			}
		}
		${DefaultCommissionsFragment}
		${MiniDealFragment}
		${ModelBasedFragment}
		${CallActivityPerformanceFragment}
		${VolumeBonusFragment}
		${BonusesTiersFragment}
		${BonusDefinitionFragment}
		${DemoAllowanceFragment}
		${PacksFragment}
		${AutoAlertsFragment}
		${TopPerformerBonusFragment}
		${CustomerSatisfactionFragment}
		${LongevityFragment}
		${UserFullNameFragment}
		${InputValueFragment}
		${DefaultCommissionsTierFragment}
		${ModelYearsFragment}
		${AdjustmentTierFragment}
		${CommissionTiersFragment}
		${MetricFragment}
	`;

	const variables = {
		locationId,
		input: {
			payPlanId: _id,
			createdBy,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.cloneSalesPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Delete selected sales pay plans
export async function deleteSalesPayPlan({ locationId, _id }) {
	const query = gql`
		mutation deleteSalesPayPlan($locationId: String!, $id: String!) {
			deleteSalesPayPlan(id: $id, locationId: $locationId) {
				_id
				locationId
			}
		}
	`;

	const variables = {
		id: _id,
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteSalesPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Update selected sales pay plans
export async function updateSalesPayPlan({ locationId, payPlan }) {
	const query = gql`
		mutation updateSalesPayPlan($locationId: String!, $input: UpdateSalesPayPlan!) {
			updateSalesPayPlan(input: $input, locationId: $locationId) {
				_id
				locationId
				name
				position
				default {
					...DefaultCommissions
				}
				miniDeal {
					...MiniDeal
				}
				modelBased {
					...ModelBased
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				volumeBonus {
					...VolumeBonus
				}
				demoAllowance {
					...DemoAllowance
				}
				packs {
					...Packs
				}
				autoAlerts {
					...AutoAlerts
				}
				topSalesBonus {
					...TopPerformerBonus
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				longevity {
					...Longevity
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					...UserFullName
				}
				updatedAt
				updatedBy {
					...UserFullName
				}
			}
		}
		${DefaultCommissionsFragment}
		${MiniDealFragment}
		${ModelBasedFragment}
		${CallActivityPerformanceFragment}
		${VolumeBonusFragment}
		${BonusDefinitionFragment}
		${DemoAllowanceFragment}
		${PacksFragment}
		${AutoAlertsFragment}
		${TopPerformerBonusFragment}
		${CustomerSatisfactionFragment}
		${LongevityFragment}
		${UserFullNameFragment}
		${InputValueFragment}
		${DefaultCommissionsTierFragment}
		${ModelYearsFragment}
		${AdjustmentTierFragment}
		${CommissionTiersFragment}
		${MetricFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		input: payPlan,
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSalesPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Save new sales pay plan
export async function saveNewSalesPayPlan({ locationId, payPlan }) {
	const query = gql`
		mutation saveNewSalesPayPlan($locationId: String!, $input: SaveSalesPayPlan!) {
			saveNewSalesPayPlan(locationId: $locationId, input: $input) {
				_id
				name
				locationId
				position
				default {
					...DefaultCommissions
				}
				miniDeal {
					...MiniDeal
				}
				modelBased {
					...ModelBased
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				volumeBonus {
					...VolumeBonus
				}
				demoAllowance {
					...DemoAllowance
				}
				packs {
					...Packs
				}
				autoAlerts {
					...AutoAlerts
				}
				topSalesBonus {
					...TopPerformerBonus
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				longevity {
					...Longevity
				}
				locked
				available
				formatVersion
				updatedAt
				updatedBy {
					...UserFullName
				}
				createdAt
				createdBy {
					...UserFullName
				}
			}
		}
		${DefaultCommissionsFragment}
		${MiniDealFragment}
		${ModelBasedFragment}
		${CallActivityPerformanceFragment}
		${VolumeBonusFragment}
		${BonusDefinitionFragment}
		${DemoAllowanceFragment}
		${PacksFragment}
		${AutoAlertsFragment}
		${TopPerformerBonusFragment}
		${CustomerSatisfactionFragment}
		${LongevityFragment}
		${UserFullNameFragment}
		${InputValueFragment}
		${DefaultCommissionsTierFragment}
		${ModelYearsFragment}
		${AdjustmentTierFragment}
		${CommissionTiersFragment}
		${MetricFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		input: payPlan,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.saveNewSalesPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}
